import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody } from 'reactstrap';
import moment from 'moment';
import { reschedulePhysiotherapyRequest } from '../../../services/api/cashless-physiotherapy-v2';

const SLOT_OPTIONS = ['Morning', 'Afternoon', 'Evening'];
const REASON_OPTIONS = [
    'Patient requested',
    'Center not available',
    'Doctor not available',
    'Other',
];

const PhysiotherapyRescheduleModal = ({ isOpen, toggle, requestId, onComplete }) => {
    const getDates = () => Array.from({ length: 15 }, (_, i) => moment().add(i, 'days'));

    const [selectedDate, setSelectedDate] = useState(getDates()[0]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedDate(getDates()[0]);
            setSelectedSlot(null);
            setReason('');
            setIsLoading(false);
            setDropdownOpen(false);
        }
    }, [isOpen, requestId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleClose = () => {
        toggle();
    };

    const handleReschedule = async () => {
        if (!selectedSlot || !reason || isLoading) return;
        setIsLoading(true);
        try {
            await reschedulePhysiotherapyRequest(requestId, {
                appointmentDate: selectedDate.format('YYYY-MM-DD'),
                reason,
                slot: selectedSlot,
            });
            toast.success('Appointment rescheduled successfully');
            onComplete();
            handleClose();
        } catch (err) {
            toast.error(err?.message || 'Error while rescheduling appointment');
        } finally {
            setIsLoading(false);
        }
    };

    const canSubmit = selectedSlot && reason && !isLoading;
    const dates = getDates();

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
            <ModalBody style={{ padding: '24px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
                        Reschedule Appointment
                    </span>
                    <button
                        onClick={handleClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '22px',
                            cursor: 'pointer',
                            color: '#666',
                            lineHeight: 1,
                            padding: 0,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Date Cards */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {dates.map((date) => {
                        const isSelected = date.isSame(selectedDate, 'day');
                        return (
                            <div
                                key={date.format('YYYY-MM-DD')}
                                onClick={() => setSelectedDate(date)}
                                style={{
                                    flex: '0 0 auto',
                                    width: '80px',
                                    padding: '14px 8px',
                                    borderRadius: '10px',
                                    border: isSelected ? 'none' : '1.5px solid #e0e0e0',
                                    backgroundColor: isSelected ? '#1a73e8' : '#f5f5f5',
                                    color: isSelected ? '#fff' : '#333',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontWeight: isSelected ? '600' : '400',
                                    transition: 'background-color 0.15s',
                                }}
                            >
                                <div style={{ fontSize: '14px' }}>{date.format('ddd, D')}</div>
                                <div style={{ fontSize: '14px' }}>{date.format('MMM')}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Slot Selection */}
                <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '10px' }}>
                        Select your preferred slot
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {SLOT_OPTIONS.map((slot) => {
                            const isSelected = selectedSlot === slot;
                            return (
                                <button
                                    key={slot}
                                    onClick={() => setSelectedSlot(slot)}
                                    style={{
                                        flex: 1,
                                        padding: '12px 8px',
                                        borderRadius: '10px',
                                        border: isSelected ? 'none' : '1.5px solid #e0e0e0',
                                        backgroundColor: isSelected ? '#1a73e8' : '#f5f5f5',
                                        color: isSelected ? '#fff' : '#333',
                                        fontWeight: isSelected ? '600' : '400',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    {slot}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Reason Custom Dropdown */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '8px' }}>
                        Reason <span style={{ color: 'red' }}>*</span>
                    </div>
                    <div ref={dropdownRef} style={{ position: 'relative' }}>
                        {/* Trigger */}
                        <div
                            onClick={() => setDropdownOpen((prev) => !prev)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderRadius: '10px',
                                border: '1.5px solid #e0e0e0',
                                fontSize: '14px',
                                color: reason ? '#333' : '#999',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <span>{reason || 'Select reason...'}</span>
                            <span style={{
                                fontSize: '11px',
                                color: '#999',
                                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                display: 'inline-block',
                            }}>▼</span>
                        </div>

                        {/* Dropdown List */}
                        {dropdownOpen && (
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                right: 0,
                                backgroundColor: '#fff',
                                border: '1.5px solid #e0e0e0',
                                borderRadius: '10px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                zIndex: 9999,
                                overflow: 'hidden',
                            }}>
                                {REASON_OPTIONS.map((r) => (
                                    <div
                                        key={r}
                                        onClick={() => {
                                            setReason(r);
                                            setDropdownOpen(false);
                                        }}
                                        style={{
                                            padding: '12px 16px',
                                            fontSize: '14px',
                                            color: '#333',
                                            cursor: 'pointer',
                                            backgroundColor: reason === r ? '#f0f4ff' : '#fff',
                                            borderBottom: '1px solid #f0f0f0',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = reason === r ? '#f0f4ff' : '#fff'; }}
                                    >
                                        {r}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={handleClose}
                        style={{
                            flex: 1,
                            padding: '13px',
                            borderRadius: '10px',
                            border: 'none',
                            backgroundColor: '#1a73e8',
                            color: '#fff',
                            fontWeight: '600',
                            fontSize: '15px',
                            cursor: 'pointer',
                        }}
                    >
                        Leave
                    </button>
                    <button
                        onClick={handleReschedule}
                        disabled={!canSubmit}
                        style={{
                            flex: 1,
                            padding: '13px',
                            borderRadius: '10px',
                            border: 'none',
                            backgroundColor: canSubmit ? '#1a73e8' : '#a8c7fa',
                            color: '#fff',
                            fontWeight: '600',
                            fontSize: '15px',
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {isLoading ? 'Rescheduling...' : 'Reschedule'}
                    </button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default PhysiotherapyRescheduleModal;
